import React from 'react';
import { Users, ArrowRightLeft, Send, Gift } from 'lucide-react';

/**
 * TokenHolders - Shared component for displaying token holder statistics
 * Used in both CoinInfo and Address pages
 *
 * @param {Object} props
 * @param {number|string|null} props.totalHolders - Total holder count or 'loading'
 * @param {Object} props.holdersByAcquisition - Breakdown by acquisition type {swap, transfer, airdrop}
 * @param {number} props.platformCount - Number of platforms aggregated (optional)
 * @param {number} props.totalPlatforms - Total platforms available (optional, for showing '+')
 */
const TokenHolders = ({
  totalHolders,
  holdersByAcquisition,
  platformCount = 0,
  totalPlatforms = 0
}) => {
  // Don't render if no data
  if (totalHolders == null) return null;

  const isLoading = totalHolders === 'loading';
  const showPlus = platformCount > 0 && totalPlatforms > 0 && platformCount < totalPlatforms;

  return (
    <div className="mb-6 pb-6 border-b border-gray-200">
      <div className="p-4 rounded-lg border-2 bg-blue-50 border-blue-300">
        <div className="flex items-center gap-2 mb-2 justify-center">
          <Users className="w-5 h-5 text-blue-600" />
          <p className="text-sm font-semibold text-gray-700">Token Holders</p>
        </div>

        <div>
          <div className="text-2xl font-bold text-blue-700 mb-3 flex items-center gap-2 justify-center">
            {isLoading ? (
              <>
                <span className="inline-block w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></span>
                <span className="text-gray-500 font-normal text-lg">Loading...</span>
              </>
            ) : (
              `${totalHolders.toLocaleString()}${showPlus ? '+' : ''}`
            )}
          </div>

          {/* Acquisition Breakdown */}
          {holdersByAcquisition && !isLoading && totalHolders > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                <ArrowRightLeft className="w-4 h-4 text-purple-600 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Swap</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {holdersByAcquisition.swap != null
                      ? `${((holdersByAcquisition.swap / totalHolders) * 100).toFixed(0)}%`
                      : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                <Send className="w-4 h-4 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Transfer</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {holdersByAcquisition.transfer != null
                      ? `${((holdersByAcquisition.transfer / totalHolders) * 100).toFixed(0)}%`
                      : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                <Gift className="w-4 h-4 text-orange-600 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Airdrop</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {holdersByAcquisition.airdrop != null
                      ? `${((holdersByAcquisition.airdrop / totalHolders) * 100).toFixed(0)}%`
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Platform info */}
          {platformCount > 0 && !isLoading && (
            <p className="text-xs text-gray-600 text-center">
              Aggregated from {platformCount} platform{platformCount > 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenHolders;
